import React, { useState, useEffect } from 'react';
import { AlertTriangle, WifiOff, RefreshCw } from 'lucide-react';
import { connectionStatus, testSupabaseConnection } from '../../lib/supabase';
import Icon from '../AppIcon';


export default function NetworkStatusBanner() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [supabaseStatus, setSupabaseStatus] = useState(connectionStatus?.supabaseReachable);
  const [isRetrying, setIsRetrying] = useState(false);
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      recheckConnection();
    };

    const handleOffline = () => {
      setIsOnline(false);
      setShowBanner(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Initial check
    if (!isOnline || connectionStatus?.supabaseReachable === false) {
      setShowBanner(true);
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    // Monitor Supabase status changes
    const interval = setInterval(() => {
      if (connectionStatus?.supabaseReachable !== supabaseStatus) {
        setSupabaseStatus(connectionStatus?.supabaseReachable);
        if (connectionStatus?.supabaseReachable === false) {
          setShowBanner(true);
        } else if (connectionStatus?.supabaseReachable === true) {
          setShowBanner(false);
        }
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [supabaseStatus]);

  const recheckConnection = async () => {
    setIsRetrying(true);
    const result = await testSupabaseConnection();
    setSupabaseStatus(result?.success ? true : false);
    setIsRetrying(false);
    
    if (result?.success) {
      setShowBanner(false);
    }
  };

  if (!showBanner) return null;

  const getStatusInfo = () => {
    if (!isOnline) {
      return {
        icon: WifiOff,
        title: 'Sem conexão com a internet',
        message: 'Verifique sua conexão de rede e tente novamente.',
        bgColor: 'bg-red-50',
        borderColor: 'border-red-200',
        textColor: 'text-red-800',
        iconColor: 'text-red-500'
      };
    }

    if (supabaseStatus === false) {
      if (connectionStatus?.projectPaused) {
        return {
          icon: AlertTriangle,
          title: 'Projeto Supabase pausado ou inacessível',
          message: 'Visite o painel do Supabase para reativar seu projeto.',
          link: 'https://supabase.com/dashboard',
          bgColor: 'bg-amber-50',
          borderColor: 'border-amber-200',
          textColor: 'text-amber-800',
          iconColor: 'text-amber-500'
        };
      }
      return {
        icon: AlertTriangle,
        title: 'Não foi possível conectar ao servidor',
        message: 'Verifique sua conexão e tente novamente.',
        bgColor: 'bg-orange-50',
        borderColor: 'border-orange-200',
        textColor: 'text-orange-800',
        iconColor: 'text-orange-500'
      };
    }

    return null;
  };

  const statusInfo = getStatusInfo();
  if (!statusInfo) return null;

  const Icon = statusInfo?.icon;

  return (
    <div className={`${statusInfo?.bgColor} border-b ${statusInfo?.borderColor} px-4 py-3`}>
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 flex-1">
          <Icon className={`w-5 h-5 ${statusInfo?.iconColor} flex-shrink-0`} />
          <div className="flex-1 min-w-0">
            <p className={`font-semibold ${statusInfo?.textColor} text-sm`}>
              {statusInfo?.title}
            </p>
            <p className={`${statusInfo?.textColor} text-xs mt-0.5`}>
              {statusInfo?.message}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2 flex-shrink-0">
          {statusInfo?.link && (
            <a
              href={statusInfo?.link}
              target="_blank"
              rel="noopener noreferrer"
              className={`text-xs font-medium ${statusInfo?.textColor} hover:underline`}
            >
              Abrir Painel
            </a>
          )}
          <button
            onClick={recheckConnection}
            disabled={isRetrying}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium ${statusInfo?.textColor} bg-white rounded-md hover:bg-opacity-80 transition-colors disabled:opacity-50`}
          >
            <RefreshCw className={`w-3.5 h-3.5 ${isRetrying ? 'animate-spin' : ''}`} />
            {isRetrying ? 'Verificando...' : 'Tentar Novamente'}
          </button>
          <button
            onClick={() => setShowBanner(false)}
            className={`text-xs font-medium ${statusInfo?.textColor} hover:underline`}
          >
            Dispensar
          </button>
        </div>
      </div>
    </div>
  );
}